import { Directive, Input, ElementRef, OnDestroy, OnChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminFeaturesService } from './service/admin-features.service';

/**
 * `<img [appAuthImg]="'2026-2027/photo.jpg'">` for anything under /upload.
 *
 * Student photographs are minors' PII, so /upload is behind the auth gate: only
 * `sponsors/`, `content/`, `blogs/`, `webcalendar/` and `events/` are public. A
 * plain `src=` is a browser navigation and carries no Authorization header, so
 * every student photo came back 401 and rendered as a broken image.
 *
 * Fetching through HttpClient lets the auth interceptor attach the token; the blob
 * is then shown from an object URL. Same approach the attendance scanner uses.
 */
@Directive({ selector: 'img[appAuthImg]' })
export class AuthImgDirective implements OnChanges, OnDestroy {
  /**
   * Path relative to the upload root, e.g. "2026-2027/abc.jpg". Blank hides the image.
   *
   * A full URL is accepted too and reduced to that same relative path. The profile
   * and coach screens already hold `setPhotoYear` - an absolute
   * `<server>/upload/<year>` - and build their src by appending the filename, so
   * taking either shape lets those templates switch over without every component
   * having to expose its year separately.
   */
  @Input('appAuthImg') path: string | null = null;

  /**
   * Shown when there is no photo, or it cannot be read.
   *
   * The profile screens print a portrait placeholder rather than an empty cell,
   * which is how those tables read in the old app - so they pass the avatar and
   * keep the row intact. Screens that would rather show nothing (the student
   * grid, Media Vault) leave this unset and the element hides itself.
   */
  @Input('appAuthImgFallback') fallback: string | null = null;

  private destroy$ = new Subject<void>();
  private objectUrl = '';

  constructor(private el: ElementRef<HTMLImageElement>, private svc: AdminFeaturesService) {}

  private miss() {
    const img = this.el.nativeElement;
    if (this.fallback) { img.src = this.fallback; img.style.visibility = 'visible'; return; }
    img.removeAttribute('src');
    img.style.visibility = 'hidden';
  }

  ngOnChanges() {
    this.release();
    const img = this.el.nativeElement;
    // Several of these templates concatenate with a stray double slash
    // ("{{setPhotoYear}}//{{photo}}"), which the storage key check rejects.
    const p = (this.path || '')
      .replace(/^.*?\/upload\//, '')
      .replace(/\/{2,}/g, '/')
      .replace(/^\/+/, '');
    // A path is only usable once BOTH halves are known. The profile screens build
    // it as "<year>/<file>", and both arrive asynchronously - a half-built path
    // like "2026-2027/undefined" would spend a request to earn a 404.
    if (!p || /(^|\/)(undefined|null)$|\/$/.test(p)) { this.miss(); return; }

    this.svc.authedImage(this.svc.uploadUrl(p)).pipe(takeUntil(this.destroy$)).subscribe(
      (blob: Blob) => {
        this.objectUrl = URL.createObjectURL(blob);
        img.src = this.objectUrl;
        img.style.visibility = 'visible';
      },
      // A missing or unreadable photo is normal - most records have none.
      () => this.miss(),
    );
  }

  private release() {
    if (this.objectUrl) { URL.revokeObjectURL(this.objectUrl); this.objectUrl = ''; }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.release();
  }
}
